import {
  type Ryecon,
  RyeconDuotoneReused,
  RyeconDuotoneStale,
  RyeconDuotoneUnknown,
  RyeconHelp,
  RyeconStatusError,
  RyeconStatusSkipped,
  RyeconStatusSuccess,
  RyeconStatusWarning,
} from '@dbt-labs/sourdough';

import {
  CostBucket,
  MaterializationType,
  ModelLayer,
  NodeStatus,
  QueryCountBucket,
  ResourceType,
  TransformationType,
} from '../../dbt-dag';

export const resourceTypesInLegend = [
  'model',
  'source',
  'snapshot',
  'seed',
  'metric',
  'semantic_model',
  'saved_query',
  'exposure',
  'function',
] as const satisfies readonly ResourceType[];
type LegendResourceType = (typeof resourceTypesInLegend)[number];

export const resourceTypeColors: Record<LegendResourceType, LensColors> = {
  exposure: {
    badge: '',
    legend: 'bg-bgDagExposure',
  },
  metric: {
    badge: '',
    legend: 'bg-bgDagMetric',
  },
  model: {
    badge: '',
    legend: 'bg-bgDagModel',
  },
  seed: {
    badge: '',
    legend: 'bg-bgDagSeed',
  },
  snapshot: {
    badge: '',
    legend: 'bg-bgDagSnapshot',
  },
  source: {
    badge: '',
    legend: 'bg-bgDagSource',
  },
  semantic_model: {
    badge: '',
    legend: 'bg-bgDagSemanticmodel',
  },
  saved_query: {
    badge: '',
    legend: 'bg-bgDagSavedquery',
  },
  function: {
    badge: '',
    legend: 'bg-bgDagFunction',
  },
};

type LensColors = {
  badge: string;
  legend: string;
};

export const materializationTypeColors: Record<
  MaterializationType | 'unknown',
  LensColors | undefined
> = {
  table: {
    badge: 'bg-bgBadgeEmeraldMuted border-borderBadgeEmeraldMuted text-fgMain',
    legend: 'bg-fgVizEmerald',
  },
  view: {
    badge: 'bg-bgBadgeIndigoMuted border-borderBadgeIndigoMuted text-fgMain',
    legend: 'bg-fgVizIndigo',
  },
  ephemeral: {
    badge: 'bg-bgBadgeAmberMuted border-borderBadgeAmberMuted text-fgMain',
    legend: 'bg-fgVizAmber',
  },
  incremental: {
    badge: 'bg-bgBadgeOrangeMuted border-borderBadgeOrangeMuted text-fgMain',
    legend: 'bg-fgVizOrange',
  },
  'materialized view': {
    badge: 'bg-bgBadgeSkyBlueMuted border-borderBadgeSkyBlueMuted text-fgMain',
    legend: 'bg-fgVizSkyBlue',
  },
  unknown: {
    badge: 'bg-bgBadgeNeutralMuted border-borderBadgeNeutralMuted text-fgMain',
    legend: 'bg-fgVizNeutral',
  },
};

export const transformationTypeColors: Record<
  TransformationType,
  LensColors | undefined
> = {
  UNKNOWN: {
    badge: 'bg-bgBadgeNeutralMuted border-borderBadgeNeutralMuted text-fgMain',
    legend: 'bg-fgVizNeutral',
  },
  RAW: {
    badge: 'bg-bgBadgeEmeraldMuted border-borderBadgeEmeraldMuted text-fgMain',
    legend: 'bg-fgVizEmerald',
  },
  PARSE_ERROR: {
    badge: 'bg-bgBadgeRedMuted border-borderBadgeRedMuted text-fgMain',
    legend: 'bg-fgVizRed',
  },
  PASSTHROUGH: {
    badge: 'bg-bgBadgeSkyBlueMuted border-borderBadgeSkyBlueMuted text-fgMain',
    legend: 'bg-fgVizSkyBlue',
  },
  TRANSFORMATION: {
    badge: 'bg-bgBadgeAmberMuted border-borderBadgeAmberMuted text-fgMain',
    legend: 'bg-fgVizAmber',
  },
  RENAME: {
    badge: 'bg-bgBadgeIndigoMuted border-borderBadgeIndigoMuted text-fgMain',
    legend: 'bg-fgVizIndigo',
  },
};

export const modelLayerColors: Record<ModelLayer | 'unknown', LensColors | undefined> =
  {
    staging: {
      badge: 'bg-bgBadgeSkyBlueMuted border-borderBadgeSkyBlueMuted text-fgMain',
      legend: 'bg-fgVizSkyBlue',
    },
    intermediate: {
      badge: 'bg-bgBadgeIndigoMuted border-borderBadgeIndigoMuted text-fgMain',
      legend: 'bg-fgVizIndigo',
    },
    marts: {
      badge: 'bg-bgBadgeEmeraldMuted border-borderBadgeEmeraldMuted text-fgMain',
      legend: 'bg-fgVizEmerald',
    },
    unknown: {
      badge: 'bg-bgBadgeNeutralMuted border-borderBadgeNeutralMuted text-fgMain',
      legend: 'bg-fgVizNeutral',
    },
  };

export const runStatusIcons: Record<NodeStatus | 'unknown', Ryecon | undefined> = {
  skipped: RyeconStatusSkipped,
  success: RyeconStatusSuccess,
  fail: RyeconStatusError,
  error: RyeconStatusError,
  warn: RyeconStatusWarning,
  unknown: RyeconHelp,
  pass: RyeconStatusSuccess,
  reused: () => (
    <RyeconDuotoneReused className="text-fgVizTeal" accentClassName="text-fgWhite" />
  ),
  unconfigured: RyeconDuotoneUnknown,
  outdated: RyeconDuotoneStale,
  'runtime error': RyeconStatusError,
};

export const nodeStatusColors: Record<NodeStatus | 'unknown', LensColors | undefined> =
  {
    skipped: {
      badge: 'bg-bgBadgeNeutralMuted border-borderBadgeNeutralMuted text-fgMain',
      legend: 'bg-fgVizNeutral',
    },
    success: {
      badge: 'bg-bgBadgeEmeraldMuted border-borderBadgeEmeraldMuted text-fgMain',
      legend: 'bg-fgVizEmerald',
    },
    fail: {
      badge: 'bg-bgBadgeRedMuted border-borderBadgeRedMuted text-fgMain',
      legend: 'bg-fgVizRed',
    },
    error: {
      badge: 'bg-bgBadgeOrangeMuted border-borderBadgeOrangeMuted text-fgMain',
      legend: 'bg-fgVizOrange',
    },
    warn: {
      badge: 'bg-bgBadgeAmberMuted border-borderBadgeAmberMuted text-fgMain',
      legend: 'bg-fgVizAmber',
    },
    reused: {
      badge: 'bg-bgBadgeTealMuted border-borderVizTeal text-fgMain',
      legend: 'bg-fgVizTeal',
    },
    unknown: {
      badge: 'bg-bgBadgeNeutralMuted border-borderBadgeNeutralMuted text-fgMain',
      legend: 'bg-fgVizNeutral',
    },
    pass: {
      badge: 'bg-bgBadgeEmeraldMuted border-borderBadgeEmeraldMuted text-fgMain',
      legend: 'bg-fgVizEmerald',
    },
    'runtime error': {
      badge: 'bg-bgBadgeOrangeMuted border-borderBadgeOrangeMuted text-fgMain',
      legend: 'bg-fgVizOrange',
    },
    unconfigured: {
      badge: 'bg-bgBadgeNeutralMuted border-borderBadgeNeutralMuted text-fgDecorative',
      legend: 'bg-fgVizNeutral',
    },
    outdated: {
      badge: 'bg-bgBadgeNeutralMuted border-borderBadgeNeutralMuted text-fgDecorative',
      legend: 'bg-fgVizNeutral',
    },
  };

export const queryCountsColors: Record<
  QueryCountBucket | 'unknown',
  LensColors | undefined
> = {
  low: {
    badge: 'bg-fgVizMonoBrand8 border-borderVizMonoBrand8 text-fgMain',
    legend: 'bg-fgVizMonoBrand8',
  },
  medium: {
    badge: 'bg-fgVizMonoBrand6 border-borderVizMonoBrand6 text-fgMain',
    legend: 'bg-fgVizMonoBrand6',
  },
  high: {
    badge: 'bg-fgVizMonoBrand4 border-borderVizMonoBrand4 text-fgInverse',
    legend: 'bg-fgVizMonoBrand4',
  },
  unknown: {
    badge: 'bg-fgVizNeutralMuted border-borderVizNeutralMuted text-fgMain',
    legend: 'bg-fgVizNeutralMuted',
  },
};

export const costColors: Record<CostBucket | 'unknown', LensColors | undefined> = {
  low: {
    badge: 'bg-fgVizMonoBrand8 border-borderVizMonoBrand8 text-fgMain',
    legend: 'bg-fgVizMonoBrand8',
  },
  medium: {
    badge: 'bg-fgVizMonoBrand6 border-borderVizMonoBrand6 text-fgMain',
    legend: 'bg-fgVizMonoBrand6',
  },
  high: {
    badge: 'bg-fgVizMonoBrand4 border-borderVizMonoBrand4 text-fgInverse',
    legend: 'bg-fgVizMonoBrand4',
  },
  unknown: {
    badge: 'bg-fgVizNeutralMuted border-borderVizNeutralMuted text-fgMain',
    legend: 'bg-fgVizNeutralMuted',
  },
};

export type ColorMap<TKey extends string> = Record<TKey, LensColors | undefined>;
